<template>
  <section class="features">
    <div v-for="feature in features" :key="feature.title" class="feature-card">
      <div class="feature-icon" v-html="feature.icon" />
      <h3>{{ feature.title }}</h3>
      <p v-html="feature.body" />
    </div>
  </section>
</template>

<script setup lang="ts">
const features = [
  {
    title: "Built-in Parser",
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>',
    body: "Custom DXF parser with zero external dependencies. 22 entity types including dimensions, hatches, splines, multilines, construction lines, regions, and block attributes. Async parsing in a Web Worker keeps the UI responsive.",
  },
  {
    title: "Vector Text",
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>',
    body: "Crisp text at any zoom level via opentype.js triangulated glyphs. Sans and serif fonts, bold and italic, stacked fractions, MTEXT formatting. Custom font loading supported.",
  },
  {
    title: "WebGL Rendering",
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>',
    body: "Three.js-powered rendering with 6 antialiasing modes (MSAA, SMAA, FXAA, TAA, SSAA, none), pan, zoom, layer visibility, instant dark theme switching, drag-and-drop, and PNG export.",
  },
  {
    title: "High Performance",
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>',
    body: "Geometry merging cuts draw calls by 78%. Block template caching, time-sliced rendering with progress bar. Text batched as geometry with all other entities.",
  },
  {
    title: "22 Entity Types",
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>',
    body: "Lines, arcs, splines, multilines, construction lines, hatches with 29 AutoCAD patterns, architectural dimensions, block inserts with attributes, leader/multileader. Variable-width polylines with per-vertex tapering, arrows, and donuts. Linetypes, OCS transforms, and paper space filtering.",
  },
  {
    title: "Picking & Associations",
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3l7 18 2-8 8-2z"/><line x1="13" y1="13" x2="21" y2="21"/></svg>',
    body: "Hover and click individual entities with raycasting. Built-in highlight overlay plus <code>entity-hover</code> / <code>entity-click</code> events for external UI sync (AG Grid, side panels). Highlight a whole semantic group automatically — MLEADER, LEADER&rarr;TEXT (DXF code 340), INSERT+ATTRIB, DIMENSION, ACAD_GROUP — through the pure-data <code>buildAssociations()</code> API.",
  },
  {
    title: "Measure & Interact",
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"/><path d="m14.5 12.5 2-2"/><path d="m11.5 9.5 2-2"/><path d="m8.5 6.5 2-2"/><path d="m17.5 15.5 2-2"/></svg>',
    body: "Measure distances, polygon areas (with perimeter) and 3-point angles with CAD-style geometry snap. Plus rectangle window/crossing selection, a read-only properties panel for the picked entity, DPI-aware rulers, and layer auto-grouping by prefix &mdash; shared across the Vue, React and Web Component wrappers.",
  },
  {
    title: "Vue, React & Web Components",
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',
    body: 'Drop-in components for <a href="https://www.npmjs.com/package/dxf-vuer" target="_blank" rel="noopener noreferrer">Vue</a>, <a href="https://www.npmjs.com/package/dxf-react" target="_blank" rel="noopener noreferrer">React</a>, and any stack via the <a href="https://www.npmjs.com/package/dxf-lit" target="_blank" rel="noopener noreferrer">&lt;dxf-viewer&gt; Web Component</a> &mdash; all on one engine. Or use <a href="https://www.npmjs.com/package/dxf-render" target="_blank" rel="noopener noreferrer">dxf-render</a> standalone with Svelte, Angular, or vanilla JS, parser-only in Node.js. Full TypeScript.',
  },
];
</script>

<style scoped>
.features {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
  max-width: var(--content-max-width);
  margin: var(--spacing-lg) auto 0;
  padding: 0;
}

.feature-card {
  padding: var(--spacing-lg);
  border: none;
  border-radius: var(--border-radius);
  background: transparent;
}

.feature-card h3 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
  color: var(--text-color);
}

.feature-card p {
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
}

.feature-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: var(--accent-bg, #f0f4ff);
  color: var(--primary-color);
  margin-bottom: var(--spacing-sm);
}

@media (max-width: 768px) {
  .features {
    grid-template-columns: 1fr;
  }
}
</style>
